import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Button from '../../components/ui/Button';
import PublicNavigation from '../../components/ui/PublicNavigation';
import { clientAuthService } from '../../services/clientAuthService';
import { supabase } from '../../lib/supabase';
import { Package, Eye, Award, RefreshCcw, User, FileText, Image, Star, Shield, LogOut, Menu, X, Gift } from 'lucide-react';
import { trackButtonClick } from '../../services/analyticsService';

import ReferralSection from './components/ReferralSection';

import { getLoyaltyLevel, getNextLevelInfo } from '../../services/referralService';


export default function ClientDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('envois');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    try {
      setLoading(true);
      setError('');

      if (!clientAuthService?.isAuthenticated()) {
        navigate('/client-login');
        return;
      }

      const clientData = JSON.parse(localStorage.getItem('client_data'));
      if (!clientData?.id || !clientData?.phone) {
        navigate('/client-login');
        return;
      }

      console.log('[RPC] calling get_client_dashboard_data', { p_client_id: clientData?.id, p_phone: clientData?.phone });

      const { data, error: rpcError } = await supabase?.rpc('get_client_dashboard_data', {
        p_client_id: clientData?.id,
        p_phone: clientData?.phone
      });

      console.log('[RPC] result', { data, error: rpcError });

      if (rpcError) {
        console.error('[RPC] error:', rpcError);
        setError('Erreur lors du chargement des données : ' + (rpcError?.message || rpcError));
        return;
      }

      if (!data) {
        setError('Aucune donnée retournée par le serveur.');
        return;
      }

      const rpcClient = data?.client || {};
      const rpcShipments = Array.isArray(data?.shipments) ? data?.shipments : [];
      const rpcFiles = Array.isArray(data?.files) ? data?.files : [];

      const invoiceFiles = rpcFiles?.filter(f => f?.file_type === 'invoice');

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const bucket = 'shipment-photos';
      const toPublicUrl = (path) => {
        if (!path) return null;
        if (path?.startsWith('http')) return path;
        return `${supabaseUrl}/storage/v1/object/public/${bucket}/${path}`;
      };

      const seen = new Set();
      const allPhotos = [];
      rpcShipments?.forEach((shipment) => {
        if (Array.isArray(shipment?.photos_urls)) {
          shipment?.photos_urls?.forEach((path) => {
            const url = toPublicUrl(path);
            if (url && !seen?.has(url)) {
              seen?.add(url);
              allPhotos?.push({ url, source: 'photos_urls', reference: shipment?.reference });
            }
          });
        }
        if (shipment?.photo_url) {
          const url = toPublicUrl(shipment?.photo_url);
          if (url && !seen?.has(url)) {
            seen?.add(url);
            allPhotos?.push({ url, source: 'photo_url', reference: shipment?.reference });
          }
        }
      });

      setClient(rpcClient);
      setShipments(rpcShipments);
      setInvoices(invoiceFiles);
      setPhotos(allPhotos);

      // Record login history
      try {
        await supabase?.from('client_login_history')?.insert({
          client_id: clientData?.id,
          login_date: new Date()?.toISOString(),
          ip_address: null,
          browser: navigator?.userAgent?.substring(0, 200) || null
        });
      } catch (histErr) {
        console.warn('Login history insert failed (non-blocking):', histErr);
      }
    } catch (err) {
      console.error('[DASHBOARD] loadClientData ERROR:', err);
      setError(err?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    trackButtonClick('refresh_client_dashboard');
    try {
      await loadClientData();
    } catch (err) {
      console.error('Refresh error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const downloadFile = async (fileUrl, fileName) => {
    try {
      if (!fileUrl) throw new Error('Invalid file URL');

      if (fileUrl?.includes('/storage/v1/object/public/')) {
        const urlParts = fileUrl?.split('/storage/v1/object/public/');
        if (urlParts?.length < 2) throw new Error('Invalid file URL');

        const [bucketPath] = urlParts?.[1]?.split('/');
        const filePath = urlParts?.[1]?.substring(bucketPath?.length + 1);

        const { data, error } = await supabase?.storage?.from(bucketPath)?.createSignedUrl(filePath, 3600);
        if (error) throw error;

        const link = document.createElement('a');
        link.href = data?.signedUrl;
        link.download = fileName;
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
      } else {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  /**
   * Safe date formatting — never hides a shipment if date is invalid.
   */
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date inconnue';
    try {
      const d = new Date(dateStr);
      if (isNaN(d?.getTime())) return 'Date inconnue';
      return d?.toLocaleDateString('fr-FR');
    } catch {
      return 'Date inconnue';
    }
  };

  const openPhotoModal = (photo) => setSelectedPhoto(photo);
  const closePhotoModal = () => setSelectedPhoto(null);

  const handleLogout = async () => {
    try {
      await clientAuthService?.signOut();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('client_data');
      localStorage.removeItem('client_session_token');
      localStorage.removeItem('fatou_client_id');
      sessionStorage.clear();
      setToastMessage('Vous avez été déconnecté avec succès.');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    }
  };

  const calculateRewardProgress = (points) => {
    const nextRewardThreshold = 50;
    const progress = Math.min((points / nextRewardThreshold) * 100, 100);
    return {
      progress,
      pointsToNext: Math.max(nextRewardThreshold - points, 0)
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'in_transit': 'bg-blue-100 text-blue-800',
      'customs': 'bg-purple-100 text-purple-800',
      'out_for_delivery': 'bg-indigo-100 text-indigo-800',
      'delivered': 'bg-green-100 text-green-800',
      'exception': 'bg-red-100 text-red-800'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'En attente',
      'in_transit': 'En transit',
      'customs': 'En douane',
      'out_for_delivery': 'En cours de livraison',
      'delivered': 'Livré',
      'exception': 'Exception'
    };
    return labels?.[status] || status || 'Inconnu';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-green-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Chargement de votre espace client...</p>
        </div>
      </div>
    );
  }

  const loyaltyProgress = calculateRewardProgress(client?.loyalty_points_total || 0);

  // Tab definitions — 'profile' and 'security' navigate away; all others are in-page tabs
  const navItems = [
    { id: 'profile', label: 'Mon profil', icon: User, navigateTo: '/client-profile' },
    { id: 'envois', label: 'Mes envois', icon: Package },
    { id: 'factures', label: 'Mes factures', icon: FileText },
    { id: 'photos', label: 'Mes photos', icon: Image },
    { id: 'fidelite', label: 'Ma fidélité', icon: Star },
    { id: 'parrainage', label: 'Parrainage', icon: Gift },
    { id: 'security', label: 'Sécurité', icon: Shield, navigateTo: '/client-profile', navigateState: { tab: 'security' } },
  ];

  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    if (item?.navigateTo) {
      navigate(item?.navigateTo, item?.navigateState ? { state: item?.navigateState } : undefined);
    } else {
      setActiveTab(item?.id);
    }
  };

  return (
    <>
      <Helmet>
        <title>Dashboard Client - ADT Tracking</title>
      </Helmet>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-pulse">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>{toastMessage}</span>
        </div>
      )}

      <div className="min-h-screen bg-gray-50 pt-16 md:pt-[136px]">
        <PublicNavigation />

        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Header Section */}
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                  Bienvenue, {client?.name || 'Client'}
                </h1>
                <p className="text-sm sm:text-base text-gray-600">{client?.email}</p>
                <p className="text-gray-600 text-sm mt-1">
                  <span className="font-medium">Adresse :</span>{' '}
                  {client?.address || 'Non renseignée'}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right mr-2">
                  <div className="text-2xl font-bold text-green-600">
                    {client?.loyalty_points_total || 0}
                  </div>
                  <div className="text-xs text-gray-600">Points fidélité</div>
                </div>
                {/* Mobile menu toggle */}
                <button
                  onClick={() => setMobileMenuOpen(v => !v)}
                  className="md:hidden flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200 transition-colors"
                >
                  {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="hidden md:flex items-center"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </div>

            {/* Navigation Menu */}
            <div className={`mt-4 pt-4 border-t border-gray-100 ${mobileMenuOpen ? 'block' : 'hidden md:block'}`}>
              <nav className="flex flex-wrap gap-2">
                {navItems?.map((item) => {
                  const ItemIcon = item?.icon;
                  const isActive = !item?.navigateTo && activeTab === item?.id;
                  return (
                    <button
                      key={item?.id}
                      onClick={() => handleNavClick(item)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        isActive
                          ? 'bg-green-600 text-white font-medium' :'text-gray-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                    >
                      <ItemIcon size={15} />
                      <span>{item?.label}</span>
                    </button>
                  );
                })}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors md:hidden"
                >
                  <LogOut size={15} />
                  <span>Déconnexion</span>
                </button>
              </nav>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2">

              {/* Mes envois */}
              {activeTab === 'envois' && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Mes envois</h2>
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 transition-colors shadow-sm min-h-[44px] text-sm sm:text-base w-full sm:w-auto"
                    >
                      <RefreshCcw className="text-white" size={18} />
                      <span>Actualiser</span>
                    </button>
                  </div>

                  {shipments?.length === 0 ? (
                    <div className="text-center py-8 sm:py-12">
                      <Package className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mb-4" />
                      <p className="text-base sm:text-lg text-gray-600">Aucun envoi pour le moment</p>
                    </div>
                  ) : (
                    <div className="space-y-3 sm:space-y-4">
                      {shipments?.map((shipment) => (
                        <div
                          key={shipment?.id}
                          className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-green-500 active:border-green-600 transition-colors"
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-2">
                            <div className="mb-2 md:mb-0">
                              <h3 className="font-semibold text-base sm:text-lg text-gray-900 mb-1 break-all">
                                {shipment?.reference}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600">
                                Créé le {formatDate(shipment?.created_at)}
                              </p>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(shipment?.current_status || shipment?.status)}`}>
                              {getStatusLabel(shipment?.current_status || shipment?.status)}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3">
                            <div>
                              <p className="text-xs sm:text-sm text-gray-600 mb-1">Destinataire</p>
                              <p className="text-sm sm:text-base font-medium text-gray-900">
                                {shipment?.receiver_name || '—'}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {shipment?.receiver_phone || ''}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs sm:text-sm text-gray-600 mb-1">Destination</p>
                              <p className="text-sm sm:text-base font-medium text-gray-900">
                                {shipment?.receiver_city || 'Abidjan'}
                              </p>
                              {shipment?.receiver_commune && (
                                <p className="text-xs sm:text-sm text-gray-600">
                                  {shipment?.receiver_commune}
                                </p>
                              )}
                            </div>
                          </div>

                          <button
                            onClick={() => navigate(`/client-shipment-detail/${shipment?.id}`, { state: { shipment } })}
                            className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors shadow-sm min-h-[44px] text-sm sm:text-base"
                          >
                            <Eye size={18} />
                            <span>Voir les détails</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Mes factures */}
              {activeTab === 'factures' && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6">Mes factures</h2>

                  {invoices?.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-600">Vous n'avez pas encore de facture disponible.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices?.map((invoice, idx) => (
                        <div
                          key={invoice?.id || idx}
                          className="border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-green-500 transition-colors"
                        >
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">
                              {invoice?.file_name || `Facture ${idx + 1}`}
                            </h3>
                            <div className="text-sm text-gray-600 space-y-1">
                              <p>
                                <span className="font-medium">Envoi :</span>{' '}
                                {invoice?.shipment_reference || invoice?.shipment_id || '—'}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadFile(invoice?.file_url, invoice?.file_name || `facture-${idx + 1}.pdf`)}
                            className="flex items-center ml-4"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Télécharger (PDF)
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Mes photos */}
              {activeTab === 'photos' && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6">Mes photos</h2>

                  {photos?.length === 0 ? (
                    <div className="text-center py-8">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-600">Aucune photo disponible pour le moment.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {photos?.map((photo, index) => (
                        <div
                          key={index}
                          className="relative group cursor-pointer"
                          onClick={() => openPhotoModal(photo)}
                        >
                          <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-green-500 transition-colors">
                            <img
                              src={photo?.url}
                              alt={`Photo ${photo?.reference}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                              <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                              </svg>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 mt-2 text-center font-medium">
                            {photo?.reference}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Ma fidélité */}
              {activeTab === 'fidelite' && (
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="flex items-center gap-2 mb-6">
                    <Award className="text-green-600" size={22} />
                    <h2 className="text-lg font-bold text-gray-900">Ma fidélité</h2>
                  </div>
                  {(() => {
                    const pts = client?.loyalty_points_total || 0;
                    const level = getLoyaltyLevel(pts);
                    const next = getNextLevelInfo(pts);
                    return (
                      <div className="space-y-4">
                        <div className={`bg-gradient-to-r ${level?.color || 'from-green-600 to-green-700'} rounded-lg p-5 text-white`}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm opacity-80">Votre niveau</p>
                              <h3 className="text-2xl font-bold">{level?.badge} {level?.label}</h3>
                            </div>
                            <div className="text-right">
                              <p className="text-4xl font-bold">{pts}</p>
                              <p className="text-sm opacity-80">points</p>
                            </div>
                          </div>
                          {next?.nextLabel && (
                            <div>
                              <div className="flex justify-between text-sm mb-1 opacity-90">
                                <span>Vers {next?.nextLabel}</span>
                                <span>{next?.pointsNeeded} pts restants</span>
                              </div>
                              <div className="w-full bg-white/20 rounded-full h-2.5">
                                <div
                                  className="bg-white rounded-full h-2.5 transition-all duration-500"
                                  style={{ width: `${Math.min(next?.progress, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                          {!next?.nextLabel && (
                            <p className="text-sm opacity-90 mt-1">🎉 Vous avez atteint le niveau maximum !</p>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { badge: '🥉', label: 'Bronze', range: '0–49 pts', active: pts < 50 },
                            { badge: '🥈', label: 'Argent', range: '50–99 pts', active: pts >= 50 && pts < 100 },
                            { badge: '🥇', label: 'Or', range: '100–199 pts', active: pts >= 100 && pts < 200 },
                            { badge: '👑', label: 'Ambassadeur', range: '200+ pts', active: pts >= 200 },
                          ]?.map((lvl) => (
                            <div key={lvl?.label} className={`rounded-lg p-3 text-center border-2 ${lvl?.active ? 'border-green-500 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
                              <p className="text-2xl mb-1">{lvl?.badge}</p>
                              <p className={`text-sm font-bold ${lvl?.active ? 'text-green-700' : 'text-gray-600'}`}>{lvl?.label}</p>
                              <p className="text-xs text-gray-500">{lvl?.range}</p>
                            </div>
                          ))}
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                          <h3 className="font-semibold text-blue-800 mb-2">Comment gagner des points ?</h3>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li>• Chaque envoi validé : points attribués automatiquement</li>
                            <li>• Parrainage d'un ami : +20 points après sa 1ère commande</li>
                            <li>• Avis Google : +10 pts (sans photo) / +15 pts (avec photo)</li>
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Parrainage */}
              {activeTab === 'parrainage' && (
                <ReferralSection client={client} />
              )}

              {/* WhatsApp Contact — shown on envois tab */}
              {activeTab === 'envois' && (
                <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4 sm:p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Besoin de commander une nouvelle barrique ou de poser une question ?
                      </h3>
                      <p className="text-gray-700 text-sm mb-4">
                        Notre équipe est disponible sur WhatsApp pour répondre à toutes vos demandes.
                      </p>
                      <a
                        href="https://wa.me/33651988621"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <Button
                          variant="primary"
                          className="bg-green-600 hover:bg-green-700 text-white flex items-center"
                        >
                          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                          </svg>
                          Nous écrire sur WhatsApp
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar - Loyalty Card */}
            <div className="lg:col-span-1">
              <div className="rounded-lg shadow-md p-4 sm:p-6 text-white" style={{ background: 'linear-gradient(135deg, #16a34a, #15803d)' }}>
                {(() => {
                  const pts = client?.loyalty_points_total || 0;
                  const level = getLoyaltyLevel(pts);
                  const next = getNextLevelInfo(pts);
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-base sm:text-lg font-bold">Ma carte fidélité</h2>
                        <Award className="text-green-200" size={24} />
                      </div>

                      {/* Level badge */}
                      <div className="bg-white/15 rounded-lg p-3 mb-3 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-green-100">Niveau actuel</p>
                          <p className="text-lg font-bold">{level?.badge} {level?.label}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl sm:text-3xl font-bold">{pts}</p>
                          <p className="text-xs text-green-100">points</p>
                        </div>
                      </div>

                      {next?.nextLabel && (
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-green-100">Vers {next?.nextLabel}</span>
                            <span className="font-medium">{next?.pointsNeeded} pts</span>
                          </div>
                          <div className="w-full bg-green-800 rounded-full h-2">
                            <div
                              className="bg-white rounded-full h-2 transition-all duration-300"
                              style={{ width: `${Math.min(next?.progress, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="bg-green-800 bg-opacity-50 rounded-lg p-4">
                        <div className="text-xs text-green-100 mb-2">Niveaux fidélité :</div>
                        <ul className="space-y-1 text-sm">
                          <li className={`flex items-center gap-2 ${pts < 50 ? 'font-bold text-white' : 'text-green-200'}`}>
                            <span>🥉</span><span>Bronze : 0–49 pts</span>
                          </li>
                          <li className={`flex items-center gap-2 ${pts >= 50 && pts < 100 ? 'font-bold text-white' : 'text-green-200'}`}>
                            <span>🥈</span><span>Argent : 50–99 pts</span>
                          </li>
                          <li className={`flex items-center gap-2 ${pts >= 100 && pts < 200 ? 'font-bold text-white' : 'text-green-200'}`}>
                            <span>🥇</span><span>Or : 100–199 pts</span>
                          </li>
                          <li className={`flex items-center gap-2 ${pts >= 200 ? 'font-bold text-white' : 'text-green-200'}`}>
                            <span>👑</span><span>Ambassadeur : 200+ pts</span>
                          </li>
                        </ul>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Quick Actions */}
              <div className="mt-6 bg-white rounded-lg shadow-md p-4 sm:p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Actions rapides</h3>
                <div className="space-y-3">
                  {shipments && shipments?.length > 0 ? (
                    <Link to={`/tracking/${shipments?.[0]?.reference}`} className="block">
                      <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-900">Suivre un envoi</span>
                        </div>
                      </button>
                    </Link>
                  ) : (
                    <div className="w-full text-left px-4 py-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-400">Aucun envoi disponible pour le moment</span>
                      </div>
                    </div>
                  )}
                  <Link to="/client-profile" className="block">
                    <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                      <div className="flex items-center">
                        <User className="w-5 h-5 text-green-600 mr-3" />
                        <span className="text-sm font-medium text-gray-900">Mon profil</span>
                      </div>
                    </button>
                  </Link>
                  <Link to="/shipping-information" className="block">
                    <button className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-900">Informations d'expédition</span>
                      </div>
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closePhotoModal}
        >
          <div
            className="relative max-w-4xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e?.stopPropagation()}
          >
            <button
              onClick={closePhotoModal}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={selectedPhoto?.url}
              alt={`Photo ${selectedPhoto?.reference}`}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            <div className="p-4 bg-gray-50 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{selectedPhoto?.reference}</p>
                {selectedPhoto?.name && (
                  <p className="text-sm text-gray-600">{selectedPhoto?.name}</p>
                )}
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={() => downloadFile(selectedPhoto?.url, selectedPhoto?.name || `photo-${selectedPhoto?.reference}.jpg`)}
                className="flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Télécharger
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}